import React, { useEffect, useRef, useCallback } from 'react';
import { ConfigProvider, Spin } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../redux/store';
import { fetchAllAdminDashboardData, setActivities, AdminDashboardState } from '../store/adminDashboardSlice';
import { useTheme } from '../../../context/ThemeContext';
import { getAntdThemeConfig } from '../../../utils/antdTheme';
import { ThemeProvider } from 'styled-components';
import { AUTO_REFRESH_INTERVAL } from './constants';
import { formatTimeAgo } from './utils';
import { Container, LoadingContainer, BottomGrid } from './styles';
import HeaderSection from './HeaderSection';
import QuickActionsSection from './QuickActionsSection';
import MetricCardsSection from './MetricCardsSection';
import RecentActivitiesSection from './RecentActivitiesSection';
import SystemHealthSection from './SystemHealthSection';

const DashboardAdmin: React.FC = () => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const dispatch = useDispatch<AppDispatch>();
  const hasFetchedRef = useRef(false);

  const {
    merchants,
    devices,
    activities,
    systemHealth,
    loading,
  } = useSelector((state: RootState): AdminDashboardState => state.adminDashboard);

  const isLoading = loading.merchants || loading.devices;

  useEffect(() => {
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      dispatch(fetchAllAdminDashboardData());
    }
  }, [dispatch]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (activities.length > 0) {
        const updated = activities.map((a) => ({
          ...a,
          time: formatTimeAgo(new Date(Date.now() - Math.random() * 30 * 60 * 1000)),
        }));
        dispatch(setActivities(updated));
      }
    }, AUTO_REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [activities, dispatch]);

  const handleRefresh = useCallback(() => {
    dispatch(fetchAllAdminDashboardData());
  }, [dispatch]);

  if (isLoading && merchants.length === 0) {
    return (
      <ThemeProvider theme={{ mode: isDarkMode ? 'dark' : 'light' }}>
        <LoadingContainer>
          <Spin size="large" />
        </LoadingContainer>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={{ mode: isDarkMode ? 'dark' : 'light' }}>
      <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
        <Container>
          <HeaderSection />
          <QuickActionsSection />

          <MetricCardsSection
            totalMerchants={merchants.length}
            activeDevices={devices.length}
            isLoading={isLoading}
          />

          <BottomGrid>
            <RecentActivitiesSection
              activities={activities}
              isLoading={loading.activities}
              onRefresh={handleRefresh}
            />
            <SystemHealthSection systemHealth={systemHealth} />
          </BottomGrid>
        </Container>
      </ConfigProvider>
    </ThemeProvider>
  );
};

export default DashboardAdmin;
